// src/ecrans/orchestrateur/EnteteOrch.jsx — L'EN-TÊTE DE L'ESPACE ORCHESTRATEUR
// (maquette v677, Orchestrateur, relevé 98) : les six tuiles d'Opérations, sans
// un montant. Porte : ecran_orch_journee_lire ; contrôles, incidents et demandes
// viennent de leurs portes déjà ouvertes. Une lecture ratée vaut « — », jamais 0.

import { useCallback, useEffect, useState } from "react";
import { fetchOrchestratorPoolIncidents } from "../../lib/incidents-api";
import { useLecturePorte } from "../portes-g3";
import { StyleG3, TuileG3, EtatG3, dateLongue, heureCourte } from "../nest-g3";

/** « 11h », « 15h et 16h », « 15h, 16h et 1 autre » ; null sans heure connue. */
export function resumeHeures(heures) {
  const hs = (heures || []).map(heureCourte).filter(Boolean);
  if (!hs.length) return null;
  if (hs.length <= 2) return hs.join(" et ");
  const reste = hs.length - 2;
  return `${hs.slice(0, 2).join(", ")} et ${reste} autre${reste > 1 ? "s" : ""}`;
}

/** Le nombre d'une lecture : « … », « — » si elle a échoué. */
export function nombreLu(lecture, calcul) {
  if (lecture.etat === "lecture") return "…";
  if (lecture.etat !== "ok") return "—";
  const n = calcul(lecture.data);
  return Number.isFinite(n) ? n : "—";
}

function useIncidentsPool(tour) {
  const [lecture, setLecture] = useState({ etat: "lecture" });
  useEffect(() => {
    let actif = true;
    fetchOrchestratorPoolIncidents({ status: "en_cours" }).then(
      (pool) => actif && setLecture(Array.isArray(pool) ? { etat: "ok", data: pool } : { etat: "echec" }),
      () => actif && setLecture({ etat: "echec" }),
    );
    return () => {
      actif = false;
    };
  }, [tour]);
  return lecture;
}

/** Même rythme que les pastilles du menu : 5 minutes, et à chaque geste. */
function useRelecture(relire) {
  useEffect(() => {
    const id = setInterval(relire, INTERVALLE_MS);
    window.addEventListener("fbz:orchestrateur-badges", relire);
    return () => {
      clearInterval(id);
      window.removeEventListener("fbz:orchestrateur-badges", relire);
    };
  }, [relire]);
}

export default function EnteteOrch({ onTab, estControleur }) {
  const [journee, relire] = useLecturePorte("ecran_orch_journee_lire");
  const [horaires, relireHoraires] = useLecturePorte("services_orchestrateur_hourly_list", SANS_OK);
  const [controles, relireControles] = useLecturePorte(estControleur ? "controle_a_controler" : null, SANS_OK);
  const [tourIncidents, setTourIncidents] = useState(0);
  const incidents = useIncidentsPool(tourIncidents);
  const toutRelire = useCallback(() => {
    relire();
    relireHoraires();
    relireControles();
    setTourIncidents((t) => t + 1);
  }, [relire, relireHoraires, relireControles]);
  useRelecture(toutRelire);
  const j = journee.etat === "ok" ? journee.data : null;
  const lu = (v) => (journee.etat === "lecture" ? "…" : v === null || v === undefined ? "—" : v);
  const nbHoraires = nombreLu(horaires, (d) => (Array.isArray(d.pending) ? d.pending.length : NaN));
  const nbControles = estControleur
    ? nombreLu(controles, (d) => (Array.isArray(d.menages) ? d.menages.length : NaN))
    : "—";
  const nbIncidents = nombreLu(incidents, (d) => d.length);
  const repli = (x) => {
    const h = resumeHeures(x.heures);
    return h ? ` · ${h}` : "";
  };
  const date = j ? dateLongue(j.aujourdhui) : "—";
  return (
    <div className="g3" data-testid="entete-orch">
      <StyleG3 />
      <p className="g3-note">Écran de l'admin, vu par l'orchestratrice : mêmes ménages, mêmes décisions, sans les montants.</p>
      <div className="g3-tete">
        <p className="g3-sous">{date} · ménages, contrôles, incidents, stock</p>
        <h2 className="g3-titre">Opérations</h2>
        <div className="g3-tuiles">
          <TuileG3 n={lu(j?.menages.faits)} sur={j?.menages.total} libelle="ménages faits" testId="orch-tuile-menages" onClick={() => onTab("menage-suivi")} />
          <TuileG3 n={lu(j?.departs.n)} libelle={`départs${j ? repli(j.departs) : ""}`} testId="orch-tuile-departs" onClick={() => onTab("menage-suivi")} />
          <TuileG3 n={lu(j?.arrivees.n)} libelle={`arrivées${j ? repli(j.arrivees) : ""}`} testId="orch-tuile-arrivees" onClick={() => onTab("menage-suivi")} />
          <TuileG3 n={nbControles} alerte={nbControles > 0} libelle={`contrôle${nbControles > 1 ? "s" : ""} à faire`} testId="orch-tuile-controles" onClick={estControleur ? () => onTab("menage-controle") : undefined} />
          <TuileG3 n={nbIncidents} alerte={nbIncidents > 0} libelle={`incident${nbIncidents > 1 ? "s" : ""} à traiter`} testId="orch-tuile-incidents" onClick={() => onTab("incidents")} />
          <TuileG3 n={nbHoraires} alerte={nbHoraires > 0} libelle={`demande${nbHoraires > 1 ? "s" : ""} d'horaire`} testId="orch-tuile-horaires" onClick={() => onTab("orchestrateur-horaires")} />
        </div>
        {journee.etat === "ok" && j.perimetre_vide ? (
          <p className="g3-manque" data-testid="orch-perimetre-vide">
            Vos logements n'ont pas pu être lus, ou aucun ne vous est confié.
          </p>
        ) : null}
        <EtatG3 lecture={journee.etat === "echec" ? journee : null} quoi="La journée" onRelire={relire} />
      </div>
    </div>
  );
}

const SANS_OK = { sansOk: true };
const INTERVALLE_MS = 5 * 60 * 1000;
